import React, { useState, useEffect } from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Table,
    Input
} from 'reactstrap';
import { CartItem } from "./Interfaces";

interface CartEditPageProps {
    isOpen: boolean;
    toggle: () => void;
    cartItems: CartItem[];
    clientName: string;
    onSaveAllChanges: (processedItems: CartItem[]) => void;
    getMinPrice: (item: CartItem) => number | null;
    isPriceEditingAllowed: (item: CartItem) => boolean;
    isDiscountEditingAllowed: (item: CartItem) => boolean;
    calculatePriceAfterDiscount: (originalPrice: number, discountPercent: number) => number;
    getDiscountError: (item: CartItem, discount: number) => boolean;
    getDecimalError: (item: CartItem) => boolean;
    getQuantityError: (item: CartItem) => boolean;
}

const CartEditPage: React.FC<CartEditPageProps> = ({
    isOpen,
    toggle,
    cartItems,
    clientName,
    onSaveAllChanges,
    getMinPrice,
    isPriceEditingAllowed,
    isDiscountEditingAllowed,
    calculatePriceAfterDiscount,
    getDiscountError,
    getDecimalError,
    getQuantityError,
}) => {
    const [editedItems, setEditedItems] = useState<CartItem[]>([]);
    const [priceErrors, setPriceErrors] = useState<Record<string, string>>({});
    const [toastError, setToastError] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen && cartItems.length > 0) {
            const itemsWithDiscount = cartItems.map(item => ({
                ...item,
                remise_percent: item.remise_percent || 0
            }));
            setEditedItems([...itemsWithDiscount]);
            setPriceErrors({});
            setToastError(null); // Clear toast when modal opens
        }
    }, [isOpen, cartItems]);

    // Auto-hide toast after 5 seconds
    useEffect(() => {
        if (toastError) {
            const timer = setTimeout(() => {
                setToastError(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [toastError]);

    const handleItemChange = (index: number, field: keyof CartItem, value: string) => {
        const updatedItems = [...editedItems];
        const currentItem = updatedItems[index];
        const minPrice = getMinPrice(currentItem);

        if (field === 'remise_percent') {
            if (!isDiscountEditingAllowed(currentItem)) {
                setToastError('Remise non autorisée pour cet article');
                return;
            }

            const discountValue = value === '' ? 0 : parseFloat(value.replace(',', '.')) || 0;
            const validatedDiscount = Math.min(Math.max(discountValue, 0), 100);

            updatedItems[index] = {
                ...updatedItems[index],
                [field]: validatedDiscount
            };

            setEditedItems(updatedItems);

        } else if (field === 'price_ttc') {
            if (!isPriceEditingAllowed(currentItem)) {
                setToastError('Modification du prix non autorisée pour cet article');
                return;
            }

            const regex = /^(\d+)([.,](\d{0,2})?)?$/;
            if (value === '' || regex.test(value)) {
                const newPrice = value === '' ? 0 : parseFloat(value.replace(',', '.')) || 0;

                setPriceErrors(prev => ({
                    ...prev,
                    [currentItem.cartItemId]: ''
                }));

                if (minPrice !== null && minPrice > 0 && newPrice < minPrice) {
                    setPriceErrors(prev => ({
                        ...prev,
                        [currentItem.cartItemId]: 'Prix non autorisé'
                    }));
                    setToastError('Prix non autorisé pour cet article');
                }

                updatedItems[index] = {
                    ...currentItem,
                    [field]: value
                };
                setEditedItems(updatedItems);
            }
        } else if (field === 'quantity') {
            const newValue = value === '' ? 0 : parseInt(value) || 0;
            updatedItems[index] = {
                ...updatedItems[index],
                [field]: newValue
            };

            if (currentItem.parentCartItemId) {
                const parentIndex = updatedItems.findIndex(
                    (item) => item.cartItemId === currentItem.parentCartItemId
                );
                if (parentIndex !== -1) {
                    updatedItems[parentIndex] = {
                        ...updatedItems[parentIndex],
                        quantity: newValue
                    };
                }
            } else {
                const consigneIndex = updatedItems.findIndex(
                    (item) => item.parentCartItemId === currentItem.cartItemId
                );
                if (consigneIndex !== -1) {
                    updatedItems[consigneIndex] = {
                        ...updatedItems[consigneIndex],
                        quantity: newValue
                    };
                }
            }
            setEditedItems(updatedItems);

        } else if (field === 'comment') {
            updatedItems[index] = {
                ...currentItem,
                [field]: value
            };
            setEditedItems(updatedItems);
        } else {
            updatedItems[index] = {
                ...updatedItems[index],
                [field]: value
            };
            setEditedItems(updatedItems);
        }
    };

    const handleSave = () => {
        const processedItems = editedItems.map(item => {
            let finalPrice = 0;
            if (typeof item.price_ttc === 'string') {
                const cleanedValue = item.price_ttc.replace(',', '.');
                finalPrice = parseFloat(cleanedValue) || 0;
                finalPrice = Math.round(finalPrice * 100) / 100;
            } else {
                finalPrice = Number(item.price_ttc) || 0;
            }

            return {
                ...item,
                price_ttc: finalPrice,
                remise_percent: item.remise_percent || 0,
                comment: item.comment || ''
            };
        });

        for (let item of processedItems) {
            const minPrice = getMinPrice(item);
            const unitPrice = Number(item.price_ttc) || 0;
            const discount = item.remise_percent || 0;
            const originalItem = cartItems.find(i => i.cartItemId === item.cartItemId);
            const originalPrice = originalItem ? Number(originalItem.price_ttc) || 0 : unitPrice;

            if (unitPrice <= 0) {
                setToastError(`Le prix de l'article ${item.articleName} doit être supérieur à 0€`);
                return;
            }

            if (!isPriceEditingAllowed(item)) {
                if (unitPrice !== originalPrice) {
                    setToastError(`Modification du prix non autorisée pour ${item.articleName}`);
                    return;
                }
            } else {
                if (minPrice !== null && minPrice > 0 && unitPrice < minPrice) {
                    setToastError(`Prix non autorisé pour ${item.articleName}`);
                    return;
                }
            }

            if (discount > 0) {
                if (!isDiscountEditingAllowed(item)) {
                    setToastError(`Remise non autorisée pour ${item.articleName}`);
                    return;
                }
                const minPrice = getMinPrice(item);
                if ((minPrice === null || minPrice === 0) && discount > 20) {
                    return;
                }

                const discountedPrice = calculatePriceAfterDiscount(originalPrice, discount);
                if (minPrice !== null && minPrice > 0 && discountedPrice < minPrice) {
                    setToastError(`Prix non autorisé pour ${item.articleName}`);
                    return;
                }
            }
            if (item.quantity < 1) {
                setToastError(`La quantité pour l'article ${item.articleName} doit être au moins 1.`);
                return;
            }
        }

        onSaveAllChanges(processedItems);
        toggle();
    };

    const calculateCartTotal = () => {
        return editedItems.reduce((sum, item) => {
            const price = typeof item.price_ttc === 'string'
                ? parseFloat(item.price_ttc.replace(',', '.')) || 0
                : Number(item.price_ttc) || 0;
            const discount = item.remise_percent || 0;

            let finalPrice = price;
            if (discount > 0) {
                finalPrice = calculatePriceAfterDiscount(price, discount);
            }

            return sum + (finalPrice * item.quantity);
        }, 0);
    };

    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            size="xl"
            scrollable
            className="cart-edit-modal"
        >
            <ModalHeader toggle={toggle}>
                <i className="ri-edit-line me-2"></i>
                Modifier le panier - {clientName}
            </ModalHeader>
            <ModalBody>
                {/* Simple Toast Error */}
                {toastError && (
                    <div className="toast-error" style={{
                        position: 'fixed',
                        top: '20px',
                        right: '20px',
                        zIndex: 9999,
                        minWidth: '300px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        padding: '12px 20px',
                        borderRadius: '4px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        animation: 'slideInRight 0.3s ease'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <i className="ri-error-warning-line me-2" style={{ fontSize: '20px' }}></i>
                            <span>{toastError}</span>
                            <button
                                onClick={() => setToastError(null)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'white',
                                    marginLeft: 'auto',
                                    cursor: 'pointer',
                                    fontSize: '18px'
                                }}
                            >
                                ×
                            </button>
                        </div>
                    </div>
                )}

                <div className="table-responsive">
                    <Table hover className="cart-edit-table">
                        <thead>
                            <tr>
                                <th>Image</th>
                                <th>Produit</th>
                                <th>Référence</th>
                                <th>Remise %</th>
                                <th>Prix (TTC)</th>
                                <th>Quantité</th>
                                <th style={{ width: '200px' }}>Commentaire</th>
                                <th>Total</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {editedItems.map((item, index) => {
                                const unitPrice = typeof item.price_ttc === 'string' ?
                                    parseFloat(item.price_ttc.replace(',', '.')) || 0 :
                                    Number(item.price_ttc) || 0;
                                const discount = item.remise_percent || 0;
                                const minPrice = getMinPrice(item);
                                const originalItem = cartItems.find(i => i.cartItemId === item.cartItemId);
                                const originalPrice = originalItem ? Number(originalItem.price_ttc) || 0 : unitPrice;

                                const priceAfterDiscount = discount > 0
                                    ? calculatePriceAfterDiscount(unitPrice, discount)
                                    : unitPrice;
                                const displayTotal = priceAfterDiscount * item.quantity;

                                const priceEditingAllowed = isPriceEditingAllowed(item);
                                const discountEditingAllowed = isDiscountEditingAllowed(item);

                                const hasQuantityError = getQuantityError(item);
                                const hasPriceError = minPrice !== null && minPrice > 0 && unitPrice < minPrice;
                                const hasDecimalError = getDecimalError(item);
                                const hasDiscountError = getDiscountError(item, discount);

                                return (
                                    <tr key={item.cartItemId}>
                                        <td className="product-image-cell">
                                            {item.product?.thumb && (
                                                <img
                                                    src={`https://api.apapiecesauto.fr/apims/imgTecdoc.php?a=${item.product.thumb}`}
                                                    alt={item.articleName}
                                                    style={{
                                                        width: '80px',
                                                        height: '80px',
                                                        objectFit: 'contain',
                                                        borderRadius: '4px',
                                                        border: '1px solid #eee',
                                                        backgroundColor: '#f8f9fa',
                                                        padding: '5px'
                                                    }}
                                                    onError={(e) => {
                                                        (e.target as HTMLImageElement).style.display = 'none';
                                                    }}
                                                />
                                            )}
                                            {item.product?.EPRimages?.[0]?.photo && !item.product?.thumb && (
                                                <img
                                                    src={`https://api.apapiecesauto.fr${item.product.EPRimagesPath || ''}${item.product.EPRimages[0].photo}`}
                                                    alt={item.product.crm?.label || item.product.tecdoc?.articleName || 'ERP Product image'}
                                                    style={{
                                                        width: '80px',
                                                        height: '80px',
                                                        objectFit: 'contain',
                                                        borderRadius: '4px',
                                                        border: '1px solid #eee',
                                                        backgroundColor: '#f8f9fa',
                                                        padding: '5px'
                                                    }}
                                                    onError={(e) => {
                                                        (e.target as HTMLImageElement).style.display = 'none';
                                                    }}
                                                />
                                            )}
                                            {!item.product?.thumb && !item.product?.EPRimages?.[0]?.photo && (
                                                <img
                                                    src="https://api.apapiecesauto.fr/apims/imgLiz.php?id=2129875&path=&extension="
                                                    alt={item.product?.crm?.label || item?.product?.tecdoc?.articleName || 'ERP Product image'}
                                                    style={{
                                                        width: '80px',
                                                        height: '80px',
                                                        objectFit: 'contain',
                                                        borderRadius: '4px',
                                                        border: '1px solid #eee',
                                                        backgroundColor: '#f8f9fa',
                                                        padding: '5px'
                                                    }}
                                                    onError={(e) => {
                                                        (e.target as HTMLImageElement).style.display = 'none';
                                                    }}
                                                />
                                            )}
                                        </td>
                                        <td>
                                            <div className="product-label-text">
                                                {item.articleName || ''}
                                            </div>
                                        </td>
                                        <td>
                                            <Input
                                                type="text"
                                                value={item.ref || ''}
                                                readOnly
                                                className="editable-field"
                                                style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                                            />
                                        </td>
                                        <td className="discount-cell">
                                            <Input
                                                type="number"
                                                min="0"
                                                max="100"
                                                step="0.01"
                                                value={discount === 0 ? '' : discount}
                                                onChange={(e) => handleItemChange(index, 'remise_percent', e.target.value)}
                                                className={`editable-field ${hasDiscountError ? 'is-invalid border-danger' : ''}`}
                                                placeholder="0"
                                                readOnly={!discountEditingAllowed}
                                                style={!discountEditingAllowed ? {
                                                    backgroundColor: '#f8f9fa',
                                                    cursor: 'not-allowed',
                                                    color: '#6c757d'
                                                } : hasDiscountError ? {
                                                    borderColor: '#dc3545',
                                                    borderWidth: '2px',
                                                    backgroundColor: '#fff'
                                                } : {}}
                                                title={!discountEditingAllowed ? "Remise non autorisée pour cet article" :
                                                    hasDiscountError ? (minPrice === null || minPrice === 0 ? "Remise max 20%" : "Valeur incorrecte") : ""}
                                            />
                                        </td>
                                        <td className="price-cell">
                                            <Input
                                                type="text"
                                                value={typeof item.price_ttc === 'string' ? item.price_ttc : (item.price_ttc?.toFixed(2) || '')}
                                                onChange={(e) => handleItemChange(index, 'price_ttc', e.target.value)}
                                                className={`editable-field ${priceErrors[item.cartItemId] ? 'is-invalid border-danger' : ''}`}
                                                placeholder="0.00"
                                                readOnly={!priceEditingAllowed}
                                                style={!priceEditingAllowed ? {
                                                    backgroundColor: '#f8f9fa',
                                                    cursor: 'not-allowed',
                                                    color: '#6c757d'
                                                } : priceErrors[item.cartItemId] ? {
                                                    borderColor: '#dc3545',
                                                    borderWidth: '2px'
                                                } : {}}
                                                title={!priceEditingAllowed ? "Modification du prix non autorisée pour cet article" :
                                                    priceErrors[item.cartItemId] ? "Prix non autorisé" : ""}
                                            />
                                        </td>
                                        <td className="quantity-cell">
                                            <Input
                                                type="number"
                                                min="1"
                                                value={item.quantity}
                                                onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                                className={`editable-field ${hasQuantityError ? 'border-danger' : ''}`}
                                                style={hasQuantityError ? {
                                                    borderColor: '#dc3545',
                                                    borderWidth: '2px'
                                                } : {}}
                                                title={hasQuantityError ? "Quantité doit être ≥ 1" : ""}
                                            />
                                        </td>
                                        <td className="commentaire-cell" style={{ minWidth: '200px' }}>
                                            <Input
                                                type="text"
                                                rows={2}
                                                value={item.comment || ''}
                                                onChange={(e) => handleItemChange(index, 'comment', e.target.value)}
                                                className="editable-field"
                                                placeholder="Ajouter un commentaire..."
                                                style={{
                                                    minWidth: '180px',
                                                    fontSize: '12px'
                                                }}
                                            />
                                        </td>
                                        <td className="total-cell">
                                            {discount > 0 ? (
                                                <div className="d-flex flex-column">
                                                    <span className="text-success fw-bold">
                                                        {displayTotal.toFixed(2)}€
                                                    </span>
                                                    <small className="text-decoration-line-through text-muted">
                                                        {(unitPrice * item.quantity).toFixed(2)}€
                                                    </small>
                                                </div>
                                            ) : (
                                                <span>{displayTotal.toFixed(2)}€</span>
                                            )}
                                        </td>
                                        <td className="actions-cell">
                                            <Button
                                                color="danger"
                                                size="sm"
                                                onClick={() => {
                                                    const updatedItems = editedItems.filter(
                                                        (i) =>
                                                            i.cartItemId !== item.cartItemId &&
                                                            i.parentCartItemId !== item.cartItemId &&
                                                            i.cartItemId !== item.parentCartItemId
                                                    );
                                                    setEditedItems(updatedItems);
                                                }}
                                                title="Supprimer"
                                            >
                                                <i className="ri-delete-bin-line"></i>
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
                <div className="cart-summary mt-3 p-3 bg-light rounded">
                    <div className="d-flex align-items-center">
                        <div className="total-price ms-auto">
                            <strong>Total panier:</strong> {calculateCartTotal().toFixed(2)}€
                        </div>
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle}>
                    Annuler
                </Button>
                <Button
                    color="primary"
                    onClick={handleSave}
                >
                    Enregistrer
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default CartEditPage;